import {kmsQueries} from '@domain/kiln-monitoring'
import {DashboardWidget, WidgetErrorBoundary} from '@hconnect/common/components'
import {GridProps} from '@mui/material'
import {useQuery} from '@tanstack/react-query'
import {useEffect} from 'react'

import {KilnProvider, SensorsProvider} from '../../providers'

import {KilnSchematicPanel} from './KilnSchematicPanel'

export interface KilnMonitoringSchematicProps {
  plantUpmId: string
  title: string
  gridProps?: Omit<GridProps, 'item' | 'zeroMinWidth'>
  onNavigateToKiln?: (kilnId: string) => void
  onSvgClick?: (kilnId: string, localNames: string[]) => void
  onMetricClick?: (kilnId: string, localNames: string[]) => void
  onCrankClick?: (kilnId: string) => void
  onTrackEvent?: (eventName: string) => void
}

const defaultGridProps: Omit<GridProps, 'item' | 'zeroMinWidth'> = {xs: 12, md: 12, lg: 6}

const KilnMonitoringSchematic = ({
  plantUpmId,
  title,
  gridProps = defaultGridProps,
  onNavigateToKiln,
  onSvgClick,
  onMetricClick,
  onCrankClick,
  onTrackEvent
}: KilnMonitoringSchematicProps) => {
  const {data: kilns, isPending: isLoadingKilns} = useQuery(kmsQueries.kilnInfo(plantUpmId))

  useEffect(() => {
    if (isLoadingKilns || !kilns?.length) return
    onTrackEvent?.('userViewsKmsWidget')
  }, [isLoadingKilns, kilns?.length, onTrackEvent])

  const createSvgClickCb = (kilnId: string) => (localNames: string[]) => {
    if (onSvgClick) {
      onSvgClick(kilnId, localNames)
    }
  }

  const createMetricClickHandler = (kilnId: string) => (localNames: string[]) => {
    if (onMetricClick) {
      onMetricClick(kilnId, localNames)
    }
  }

  if (isLoadingKilns) {
    return (
      <DashboardWidget title={title} gridProps={gridProps} loading minWidth={330}>
        <div />
      </DashboardWidget>
    )
  }

  return (
    <>
      {kilns?.map((kiln) => (
        <DashboardWidget
          key={kiln.id}
          title={`${title} (${kiln.name || '-'})`}
          gridProps={gridProps}
          minWidth={330}
          onClick={() => onNavigateToKiln?.(kiln.id)}
          data-test-id={`kiln-monitoring-schematic-widget-${kiln.id}`}
        >
          <KilnProvider kilnId={kiln.id} plantUpmId={plantUpmId}>
            <SensorsProvider plantUpmId={plantUpmId}>
              <KilnSchematicPanel
                hoveredSignal={null}
                svgClickCb={createSvgClickCb(kiln.id)}
                onKilnMetricClick={createMetricClickHandler(kiln.id)}
                onCrankClick={onCrankClick}
                showMaterialFlow={false}
              />
            </SensorsProvider>
          </KilnProvider>
        </DashboardWidget>
      ))}
    </>
  )
}

export const KilnMonitoringSchematicWidget = ({
  gridProps = defaultGridProps,
  ...props
}: KilnMonitoringSchematicProps) => (
  <WidgetErrorBoundary gridProps={gridProps}>
    <KilnMonitoringSchematic gridProps={gridProps} {...props} />
  </WidgetErrorBoundary>
)
